import React, { memo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Award, Pencil, Trash2 } from "lucide-react";
import { NATIONALITIES, flagUrl } from "@/lib/nationalities";

const NAT_MAP = Object.fromEntries(NATIONALITIES.map((n) => [n.code, n]));

function NatLabel({ code }) {
  const cur = NAT_MAP[code];
  return (
    <span className="inline-flex items-center gap-2">
      {flagUrl(code) && <img src={flagUrl(code)} alt={code} className="w-4 h-3 object-cover rounded-sm" />}
      {cur ? cur.label : code}
    </span>
  );
}

// Only mounts the 250 country items while the dropdown is open. Radix renders
// closed <SelectContent> children into a hidden fragment, so a table with N rows
// was re-rendering N×250 items on every keystroke elsewhere on the page.
export const NationalityPicker = memo(function NationalityPicker({ value, onChange, className, testid }) {
  const [open, setOpen] = useState(false);
  const code = (value || "PT").toUpperCase();
  return (
    <Select value={code} onValueChange={onChange} open={open} onOpenChange={setOpen}>
      <SelectTrigger className={className} data-testid={testid}>
        <SelectValue><NatLabel code={code} /></SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-[260px]">
        {open
          ? NATIONALITIES.map((n) => (
              <SelectItem key={n.code} value={n.code}><NatLabel code={n.code} /></SelectItem>
            ))
          : <SelectItem value={code}><NatLabel code={code} /></SelectItem>}
      </SelectContent>
    </Select>
  );
});

export const CredentialRow = memo(function CredentialRow({
  c, rank, isSel, levelLabel, used, allowed,
  onToggleSelect, onLocalChange, onCommit, onDiploma, onEdit, onRemove,
}) {
  return (
    <tr className={`border-b hover:bg-slate-50 ${isSel ? "bg-rose-50" : ""}`}>
      <td className="px-3 py-2">
        <input
          type="checkbox"
          data-testid={`cred-select-${c.id}`}
          className="w-4 h-4 cursor-pointer"
          checked={isSel}
          onChange={() => onToggleSelect(c.id)}
        />
      </td>
      <td className="px-3 py-2 font-mono">{c.username}</td>
      <td className="px-3 py-2 font-mono">{c.password}</td>
      <td className="px-3 py-2">
        <Input className="h-8 min-w-[120px]" value={c.name || ""} onChange={(e) => onLocalChange(c.id, { name: e.target.value })} onBlur={(e) => onCommit(c.id, { name: e.target.value })} />
      </td>
      <td className="px-3 py-2">
        <Input className="h-8 min-w-[120px]" value={c.school || ""} onChange={(e) => onLocalChange(c.id, { school: e.target.value })} onBlur={(e) => onCommit(c.id, { school: e.target.value })} />
      </td>
      <td className="px-3 py-2">{c.email}</td>
      <td className="px-3 py-2">
        <NationalityPicker
          value={c.nationality}
          className="h-8 min-w-[130px]"
          onChange={(v) => { onLocalChange(c.id, { nationality: v }); onCommit(c.id, { nationality: v }); }}
        />
      </td>
      <td className="px-3 py-2" data-testid={`cred-level-${c.id}`}>{levelLabel}</td>
      <td className="px-3 py-2 font-mono">{c.best_time != null ? `${c.best_time.toFixed(1)}s` : "-"}</td>
      <td className="px-3 py-2" data-testid={`rank-${c.id}`}>
        {rank ? (
          <span className={`inline-flex items-center justify-center min-w-[28px] h-6 rounded-full text-xs font-bold ${
            rank === 1 ? "bg-amber-100 text-amber-700 ring-1 ring-amber-400" :
            rank === 2 ? "bg-slate-200 text-slate-700" :
            rank === 3 ? "bg-orange-100 text-orange-700" :
            "bg-slate-100 text-slate-600"
          }`}>{rank}</span>
        ) : (
          <span className="text-slate-400 text-xs">—</span>
        )}
      </td>
      <td className="px-3 py-2" data-testid={`used-attempts-${c.id}`}>
        <span className="inline-flex items-center justify-center min-w-[28px] h-6 rounded-full text-xs font-bold bg-slate-100 text-slate-700" title={`${used} de ${allowed} permitidas`}>
          {used}
        </span>
      </td>
      <td className="px-3 py-2">
        <Input
          type="number"
          min={0}
          className="h-8 w-16"
          value={c.extra_attempts ?? 0}
          onChange={(e) => onLocalChange(c.id, { extra_attempts: +e.target.value || 0 })}
          onBlur={(e) => onCommit(c.id, { extra_attempts: parseInt(e.target.value || "0", 10) })}
          data-testid={`extra-attempts-${c.id}`}
        />
      </td>
      <td className="px-3 py-2">{c.victories}</td>
      <td className="px-3 py-2">{c.defeats}</td>
      <td className="px-3 py-2">
        <button
          data-testid={`btn-diploma-${c.id}`}
          onClick={() => onDiploma(c)}
          className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 font-medium text-xs"
          title="Ver diploma"
        >
          <Award className="w-4 h-4" /> Ver
        </button>
      </td>
      <td className="px-3 py-2">
        <div className="inline-flex items-center gap-1">
          <button data-testid={`btn-edit-cred-${c.id}`} onClick={() => onEdit(c)} className="text-sky-600 hover:text-sky-800" title="Editar">
            <Pencil className="w-4 h-4" />
          </button>
          <button data-testid={`btn-delete-cred-${c.id}`} onClick={() => onRemove(c.id)} className="text-rose-600 hover:text-rose-800" title="Eliminar">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
});
